import {
  CaseSizeRuleCreateRequestType,
  PriceTierListResponseType,
} from "@sellar-io/api-v2/types";
import { getDiscountDescription } from "@sellar-io/common";
import {
  Button,
  H5,
  Icon,
  useFullPageDialogContext,
  InputPercentage,
  InputNote,
  InputNumber,
  InputCurrency,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "@sellar-io/components";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { Alert } from "../../../../../components/alert/Alert";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../../../../components/table/TableLegacy";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { useCaseSizeRuleCreate } from "../../../../../hooks/api/v2/caseSizeRule/useCaseSizeRuleCreate";
import { usePriceTierList } from "../../../../../hooks/api/v2/priceTier/usePriceTierList";
import { usePriceTierDiscountUpdate } from "../../../../../hooks/api/v2/priceTierDiscount/usePriceTierDiscountUpdate";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../../../hooks/usePriceFormat";
import { generateProductItemName } from "../../../helpers/generateProductItemName";
import { CaseSizeRuleCreateDialogPage } from "../CaseSizeRuleCreateDialogPage";
import { getPriceTiersWithDiscountContainerType } from "../helpers/priceTierDiscountRules";
import { useRemoteFeatureFlags } from "../../../../../hooks/useRemoteFeatureFlags";

type UpdatePriceTierDiscountFormInputs = {
  discountByType: "minimumOrderQuantity" | "minimumBasketValue";
  type: PriceTierListResponseType[number]["PriceTierDiscounts"][number]["type"];
  minimumBasketValue: number;
  itemType:
    | PriceTierListResponseType[number]["PriceTierDiscounts"][number]["itemType"]
    | "any";
  quantity: number;
  amount: number;
};

const sortByIdAsc = <T extends { id: number }>(a: T, b: T) => a.id - b.id;

const usePriceTierListWithDiscountList = ({
  containerType,
}: {
  containerType: "bottle" | "can";
}) => {
  const priceTiersListQuery = usePriceTierList({
    sortBy: "name",
    sortDir: "asc",
  });
  const priceTierList = priceTiersListQuery.data?.pages[0] ?? [];
  const priceTierDiscountList = getPriceTiersWithDiscountContainerType({
    priceTierList,
    containerType,
  });
  {
    return {
      priceTierDiscountList: priceTierDiscountList,
    };
  }
};

const CaseSizeRuleLegend = ({
  caseSizeRule,
  className,
}: {
  caseSizeRule: CaseSizeRuleCreateRequestType;
  className?: string;
}) => {
  return (
    <div className={twMerge("flex rounded-lg bg-gray-100 p-4", className)}>
      <H5 className="inline">1 case =</H5>&nbsp;
      {caseSizeRule.quantity}&nbsp;x&nbsp;
      {generateProductItemName(caseSizeRule)}
    </div>
  );
};

const TableRow = ({
  priceTierName,
  priceTierDiscountList,
  onExpandPriceTierDiscount,
}: {
  priceTierName: string;
  priceTierDiscountList: PriceTierListResponseType[number]["PriceTierDiscounts"];
  onExpandPriceTierDiscount: (
    priceTierDiscount: PriceTierListResponseType[number]["PriceTierDiscounts"][number],
  ) => void;
}) => {
  return (
    <TableLegacyTR>
      <TableLegacyTD>{priceTierName}</TableLegacyTD>
      <TableLegacyTD>
        <span className="flex flex-col">
          {priceTierDiscountList
            .sort(sortByIdAsc)
            .map((priceTierDiscount, index) => {
              const discountAsDescription = getDiscountDescription({
                discount: priceTierDiscount,
              });

              return (
                <span key={index} className="flex justify-between">
                  <span className="flex items-center">
                    {discountAsDescription}
                  </span>
                  <span>
                    <Button
                      color={"transparent"}
                      onClick={() =>
                        onExpandPriceTierDiscount(priceTierDiscount)
                      }
                    >
                      <Icon icon={"CHEVRON_RIGHT"} />
                    </Button>
                  </span>
                </span>
              );
            })}
        </span>
      </TableLegacyTD>
    </TableLegacyTR>
  );
};

const PriceTiersWithVolumeDiscountsList = ({
  caseSizeRule,

  onDiscountSelected,
}: {
  caseSizeRule: CaseSizeRuleCreateRequestType;
  onDiscountSelected: (
    priceTierDiscount: PriceTierListResponseType[number]["PriceTierDiscounts"][number],
  ) => void;
}) => {
  const { priceTierDiscountList } = usePriceTierListWithDiscountList({
    containerType: caseSizeRule.containerType,
  });

  const unbundlingRelease2BEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2B",
  );

  return (
    <>
      {priceTierDiscountList.length ? (
        <>
          <CaseSizeRuleLegend className="mb-5" caseSizeRule={caseSizeRule} />
          <TableLegacyContainer>
            <TableLegacy>
              <TableLegacyTHead>
                <TableLegacyTR>
                  <TableLegacyTH>
                    {unbundlingRelease2BEnabled ? "Price list" : "Price tier"}
                  </TableLegacyTH>
                  <TableLegacyTH>Volume Discount</TableLegacyTH>
                </TableLegacyTR>
              </TableLegacyTHead>

              <TableLegacyTBody>
                {priceTierDiscountList.map((priceTierWithDiscountList) => {
                  return (
                    <TableRow
                      key={priceTierWithDiscountList.id}
                      priceTierName={priceTierWithDiscountList.name}
                      priceTierDiscountList={
                        priceTierWithDiscountList.PriceTierDiscounts
                      }
                      onExpandPriceTierDiscount={(discount) =>
                        onDiscountSelected(discount)
                      }
                    />
                  );
                })}
              </TableLegacyTBody>
            </TableLegacy>
          </TableLegacyContainer>
        </>
      ) : (
        <H5>
          You do not have any volume discounts set for this product item type.
        </H5>
      )}
    </>
  );
};

const UpdatePriceTierDiscountForm = ({
  caseSizeRule,
  priceTierDiscount,
  onBack,
}: {
  caseSizeRule: CaseSizeRuleCreateRequestType;
  priceTierDiscount: PriceTierListResponseType[number]["PriceTierDiscounts"][number];
  onBack: () => void;
}) => {
  const { register, handleSubmit, reset, control } =
    useForm<UpdatePriceTierDiscountFormInputs>({
      shouldUseNativeValidation: true,
    });

  const discountByType =
    priceTierDiscount.minimumBasketValue > 0
      ? "minimumBasketValue"
      : "minimumOrderQuantity";

  const discountType = useWatch({ control, name: "type" });
  const priceTierOrderRuleUpdateMutation = usePriceTierDiscountUpdate(
    priceTierDiscount.id,
  );

  useEffect(() => {
    reset({
      ...priceTierDiscount,
      itemType: priceTierDiscount.itemType ?? "any",
      amount:
        priceTierDiscount.type === "percentage"
          ? priceTierDiscount.amount
          : parseInt(formatPriceApiToInput(priceTierDiscount.amount)),
      minimumBasketValue: parseInt(
        formatPriceApiToInput(priceTierDiscount.minimumBasketValue),
      ),
    });
  }, [reset, priceTierDiscount]);

  return (
    <>
      <form
        onSubmit={handleSubmit((data) => {
          const amount =
            data.type === "percentage"
              ? data.amount
              : (formatPriceInputToApi(data.amount) ?? 0);

          const itemType = data.itemType === "any" ? null : data.itemType;

          priceTierOrderRuleUpdateMutation.mutate(
            {
              ...data,
              itemType,
              amount,
              ...(data.quantity
                ? {
                    quantity: data.quantity,
                    minimumBasketValue: 0,
                  }
                : {
                    minimumBasketValue:
                      formatPriceInputToApi(data.minimumBasketValue) ?? 0,
                    quantity: 0,
                  }),
            },
            {
              onSuccess: () => {
                onBack();
              },
            },
          );
        })}
      >
        <DialogNavigation
          title={"Edit volume discount"}
          leftIcon={"CHEVRON_LEFT"}
          onClickLeftIcon={onBack}
        />

        <DialogContent className={"flex flex-col gap-4"}>
          {priceTierOrderRuleUpdateMutation.isError ? (
            <Alert
              type={"error"}
              className={"mb-5 w-full"}
              message={
                apiV2AssertError(priceTierOrderRuleUpdateMutation.error)
                  ?.response?.data.message ?? "Unknown error"
              }
            />
          ) : null}

          <CaseSizeRuleLegend className="mb-5" caseSizeRule={caseSizeRule} />

          <div>
            <InputLabel>Container type</InputLabel>
            <InputContainer>
              <>
                <Icon icon={"LOCK"} size={1.1} className={"text-neutral-300"} />
                <InputSelect
                  {...register("itemType", { required: true })}
                  disabled
                >
                  <InputOption key={"any"} value={"any"}>
                    Any
                  </InputOption>
                  <InputOption value={"bottle"}>Cases of bottles</InputOption>
                  <InputOption value={"can"}>Cases of cans</InputOption>
                </InputSelect>
              </>
            </InputContainer>
          </div>

          {discountByType === "minimumOrderQuantity" && (
            <div>
              <InputLabel>Container quantity</InputLabel>
              <InputContainer>
                <InputNumber
                  {...register("quantity", {
                    required: true,
                    min: 1,
                    valueAsNumber: true,
                  })}
                  placeholder={"5"}
                />
              </InputContainer>
            </div>
          )}

          {discountByType === "minimumBasketValue" && (
            <div>
              <InputLabel>Minimum basket value</InputLabel>
              <InputContainer>
                <InputCurrency
                  showPrefix
                  allowNegative
                  {...register("minimumBasketValue", {
                    required: true,
                    min: 1,
                  })}
                  placeholder="250"
                />
              </InputContainer>
              <InputNote>Minimum £0.01</InputNote>
            </div>
          )}

          <div>
            <InputLabel>Discount type</InputLabel>
            <InputContainer>
              <>
                <Icon icon={"LOCK"} size={1.1} className={"text-neutral-300"} />
                <InputSelect {...register("type", { required: true })} disabled>
                  <InputOption value={"percentage"}>Percentage</InputOption>
                  <InputOption value={"perpetual-flat"}>
                    Flat (per item)
                  </InputOption>
                </InputSelect>
              </>
            </InputContainer>
          </div>

          {discountType === "percentage" ? (
            <div>
              <InputLabel>Discount percentage</InputLabel>
              <InputContainer>
                <InputPercentage
                  placeholder={"15%"}
                  step={"0.1"}
                  {...register("amount", {
                    min: 1,
                    max: 100,
                    required: true,
                    valueAsNumber: true,
                  })}
                />
                <span>%</span>
              </InputContainer>
            </div>
          ) : (
            <div>
              <InputLabel>Discount value</InputLabel>
              <InputContainer>
                <InputCurrency
                  showPrefix
                  allowNegative
                  placeholder={"10"}
                  {...register("amount", {
                    required: true,
                    valueAsNumber: true,
                  })}
                />
              </InputContainer>
            </div>
          )}
        </DialogContent>
        <DialogCTA>
          <Button
            type="submit"
            loading={priceTierOrderRuleUpdateMutation.isPending}
          >
            Update
          </Button>
        </DialogCTA>
      </form>
    </>
  );
};

export const VolumeDiscountRules = ({
  caseSizeRule,
}: {
  caseSizeRule: CaseSizeRuleCreateRequestType;
}) => {
  const { setPage, onDismiss } = useFullPageDialogContext();
  const caseSizeRuleCreateMutation = useCaseSizeRuleCreate();
  const [selectedPriceTierDiscount, setSelectedPriceTierDiscount] = useState<
    PriceTierListResponseType[number]["PriceTierDiscounts"][number] | undefined
  >();

  const onCreateCaseSizeRule = () => {
    caseSizeRuleCreateMutation.mutate(caseSizeRule, {
      onSuccess: () => {
        onDismiss?.();
      },
    });
  };

  return (
    <>
      {selectedPriceTierDiscount ? (
        <UpdatePriceTierDiscountForm
          caseSizeRule={caseSizeRule}
          priceTierDiscount={selectedPriceTierDiscount}
          onBack={() => setSelectedPriceTierDiscount(undefined)}
        />
      ) : (
        <>
          <DialogNavigation
            title={"Add volume discounts rule"}
            description={`The following volume discounts set up refer to a ‘case’ of this product item. Are these still correct?`}
            leftIcon={"CHEVRON_DOWN"}
            onClickLeftIcon={() => onDismiss?.()}
          />
          <DialogContent>
            {caseSizeRuleCreateMutation.isError ? (
              <Alert
                type={"error"}
                className={"mb-5 w-full"}
                message={
                  apiV2AssertError(caseSizeRuleCreateMutation.error)?.response
                    ?.data.message ?? "Unknown error"
                }
              />
            ) : null}
            <PriceTiersWithVolumeDiscountsList
              caseSizeRule={caseSizeRule}
              onDiscountSelected={(priceTierDiscount) => {
                setSelectedPriceTierDiscount(priceTierDiscount);
              }}
            />
          </DialogContent>
          <DialogCTA>
            <Button
              color={"transparent"}
              onClick={() =>
                setPage(CaseSizeRuleCreateDialogPage.UPDATE_MOQ_RULES)
              }
            >
              Back
            </Button>
            <Button
              onClick={onCreateCaseSizeRule}
              loading={caseSizeRuleCreateMutation.isPending}
            >
              Confirm
            </Button>
          </DialogCTA>
        </>
      )}
    </>
  );
};
